import React from 'react';
import { Card, Grid, Typography } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import { DisplayStringTrans, useTranslation } from '@gamiphy/react-i18n';
import { ThemeStore } from '@gamiphy/loyalty-station-desktop/store/theme';
import { AppStore } from '@gamiphy/loyalty-station-desktop/store/app';
import { formatCash } from '@gamiphy/utils/currency';
import { getGemsValue } from '@gamiphy/bl/reward';
import { DefaultIconProps } from '@gamiphy/icons/types';
import GiftIcon from '@gamiphy/icons/GiftIcon';
import CashbackIcon from '@gamiphy/icons/CashbackIcon';
import PlayingIcon from '@gamiphy/icons/PlayingIcon';
import { WelcomeGiftStore, WelcomeGiftTypes } from '@gamiphy/loyalty-station-desktop/store/welcomeGift';
import { observer } from 'mobx-react';
import { useTheme } from '@mui/material/styles';
import { DiscountStore } from '@gamiphy/loyalty-station-desktop/store/reward/discount';
import { compact } from '@gamiphy/utils/array';
import { AppWelcomeGiftStore } from '@gamiphy/loyalty-station-desktop/store/appWelcomeGift';
import { formatWelcomeGiftValue } from '@gamiphy/loyalty-station-desktop/components/cashback/utils';
import { DisplayString } from '@coretava/i18n';
import Actions from './Actions';

type Benefits = {
  text: string,
  Icon: React.FC<DefaultIconProps>,
  i18nKey: string,
  id: string,
};

const Welcome: React.FC = observer(() => {
  const { t } = useTranslation();
  const theme = useTheme();

  const freePointsValue = React.useMemo<string | undefined>(() => {
    switch (WelcomeGiftStore.type) {
      case WelcomeGiftTypes.discount:
        return AppWelcomeGiftStore.welcomeGift?.enabled
          ? formatWelcomeGiftValue(AppWelcomeGiftStore.welcomeGift)
          : undefined;
      case WelcomeGiftTypes.points:
        return WelcomeGiftStore.total
          ? formatCash(getGemsValue(WelcomeGiftStore.total, DiscountStore.appDiscount))
          : undefined;
      default:
        return undefined;
    }
  }, [WelcomeGiftStore.total,
    WelcomeGiftStore.type,
    AppWelcomeGiftStore.welcomeGift,
    DiscountStore.appDiscount?.value]);

  const benefits = React.useMemo<Benefits[]>(() => (compact([
    freePointsValue &&
    {
      text: t('landing.benefits.gifts', {
        defaultValue: 'Earn a welcome gift of {{value}} for your first purchase',
        value: freePointsValue,
      }),
      Icon: GiftIcon,
      i18nKey: 'landing.benefits.gifts',
      id: uuidv4(),
    },
    {
      text: t('landing.benefits.cashback', {
        defaultValue: 'Earn cashback on every purchase you make',
      }),
      Icon: CashbackIcon,
      i18nKey: 'landing.benefits.cashback',
      id: uuidv4(),
    },
    {
      text: t('landing.benefits.playing', {
        defaultValue: 'Enjoy playing and level up in the experience',
      }),
      Icon: PlayingIcon,
      i18nKey: 'landing.benefits.playing',
      id: uuidv4(),
    },
  ])), [t, freePointsValue]);

  return (
    <Grid container direction="column">
      <Grid sx={{ width: '100%' }}>
        <Grid sx={{ padding: theme.spacing(2), marginTop: '-7rem' }}>
          <Card variant="elevation" sx={{ borderRadius: '1rem', boxShadow: '0 5px 10px 0 rgba(0, 0, 0, 0.08)', padding: theme.spacing(2, 2) }}>
            <Grid>
              <Typography
                sx={{
                  fontSize: '1.4rem', fontWeight: 600, textAlign: 'center', marginBottom: theme.spacing(2),
                }}
                variant="h6"
                component="h6"
              >
                {t('landing.intro.header.ls2', {
                  defaultValue: ' Become a member',
                })}
              </Typography>
            </Grid>
            <Grid sx={{ margin: theme.spacing(2, 0), textAlign: 'center', overflowWrap: 'break-word' }}>
              {
                ThemeStore.theme?.onboarding?.landing?.message && (
                  <DisplayStringTrans
                    displayString={ThemeStore.theme.onboarding.landing.message as DisplayString}
                    values={{
                      name: AppStore.app?.name ? (AppStore.app?.name) : (
                        t('onboarding.landingPage.branding.greeting', 'Loyalty Station Name')
                      ),
                    }}
                  />
                )
              }
            </Grid>
            <Grid sx={{ display: 'flex', justifyContent: 'center' }}>
              <Actions />
            </Grid>
          </Card>
        </Grid>
        <Grid sx={{ padding: theme.spacing(2, 2, 1) }}>
          <Card variant="elevation" sx={{ borderRadius: '1rem', boxShadow: '0 5px 10px 0 rgba(0, 0, 0, 0.08)', padding: theme.spacing(2, 0) }}>
            <Grid>
              <Typography
                sx={{
                  fontSize: '1.4rem', fontWeight: 600, textAlign: 'center', marginBottom: theme.spacing(2),
                }}
                variant="h6"
                component="h6"
              >
                {t('shared.benefits', {
                  defaultValue: 'Benefits',
                })}
              </Typography>
            </Grid>
            <Grid container direction="column" alignItems="center" justifyContent="center">
              {benefits.map(({ text, Icon, id }) => (
                <Grid
                  key={id}
                  container
                  direction="row"
                  alignItems="center"
                  wrap="nowrap"
                  sx={{ padding: theme.spacing(0, 3), marginBottom: theme.spacing(2) }}
                  spacing={1}
                >
                  <Grid>
                    <Icon
                      width={24}
                      height={24}
                      textColor={theme.palette.primary.main}
                      style={{ maxWidth: '2.5rem', maxHeight: '2.5rem', marginRight: theme.spacing(1) }}
                    />
                  </Grid>
                  <Grid sx={{ fontSize: '1.2rem', fontWeight: 400, textAlign: 'left' }}>
                    <Typography variant="subtitle1">
                      {text}
                    </Typography>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Card>
        </Grid>
      </Grid>
    </Grid>
  );
});

export default Welcome;
